// --- Core ---
import React from "react";
import PropTypes from "prop-types";
import {FormattedMessage} from "react-intl";
import {addDays, endOfDay, endOfMonth, endOfYear, isValid, startOfDay, startOfMonth, startOfYear, subDays, subMonths, subYears} from "date-fns";
// --- Material UI ---
import {Grid, MenuItem} from "@mui/material";
// --- SaniQ ---
import {SelectField} from "helpers/Forms";
import DateSelect from "./DateSelect";

export const rangeToInterval = (range) => {
  const {dateRanges} = DateRangeSelect;
  let from, to = null;

  switch (range) {
    case dateRanges.currentMonth:
      from = startOfMonth(new Date());
      to = endOfDay(new Date());
      break;
    case dateRanges.lastMonth:
      from = subMonths(startOfMonth(new Date()), 1);
      to = subMonths(endOfMonth(new Date()), 1);
      break;
    case dateRanges.last30Days:
      from = subDays(startOfDay(new Date()), 30);
      to = endOfDay(new Date());
      break;
    case dateRanges.last60Days:
      from = subDays(startOfDay(new Date()), 60);
      to = endOfDay(new Date());
      break;
    case dateRanges.last90Days:
      from = subDays(startOfDay(new Date()), 90);
      to = endOfDay(new Date());
      break;
    case dateRanges.last6Months:
      from = subMonths(startOfDay(new Date()), 6);
      to = endOfDay(new Date());
      break;
    case dateRanges.last12Months:
      from = subMonths(startOfDay(new Date()), 12);
      to = endOfDay(new Date());
      break;
    case dateRanges.currentYear:
      from = startOfYear(new Date());
      to = endOfDay(new Date());
      break;
    case dateRanges.lastYear:
      from = subYears(startOfYear(new Date()), 1);
      to = subYears(endOfYear(new Date()), 1);
      break;
    default:
      break;
  }

  return {
    from: from,
    to: to
  };
};

export default class DateRangeSelect extends React.Component {
  static propTypes = {
    onChange: PropTypes.func.isRequired,
    spacing: PropTypes.number,
    dateInitialRange: PropTypes.string,
    withTimeRange: PropTypes.bool,
    maxRangeInDays: PropTypes.number,
    initialTimeInterval: PropTypes.shape({
      range: PropTypes.string.isRequired,
      from: PropTypes.instanceOf(Date).isRequired,
      to: PropTypes.instanceOf(Date).isRequired
    }),
    meta: PropTypes.shape({
      submitError: PropTypes.object,
      submitFailed: PropTypes.bool
    })
  };

  static dateRanges = {
    currentMonth: "curM",
    lastMonth: "lastM",
    last30Days: "30D",
    last60Days: "60D",
    last90Days: "90D",
    last6Months: "6M",
    last12Months: "12M",
    currentYear: "curY",
    lastYear: "lastY",
    customRange: "custom"
  };

  static defaultProps = {
    spacing: 2,
    withTimeRange: true,
    maxRangeInDays: -1, // unlimited max range
    dateInitialRange: DateRangeSelect.dateRanges.customRange
  };

  constructor(props, context) {
    super(props, context);
    this.updateDates = this.updateDates.bind(this);
    if (props.initialTimeInterval) {
      this.state = {
        ...props.initialTimeInterval
      };
    } else {
      this.state = {
        range: props.dateInitialRange,
        from: null,
        to: null
      };
    }
  }

  componentDidMount() {
    this.updateDates(this.state.range);
  }

  shouldUpdate(date, prevDate) {
    return (date && isValid(date) && (date !== prevDate));
  }


  componentDidUpdate(prevProps, prevState, snapshot) {
    const {from, range, to} = this.state;

    if (this.shouldUpdate(from, prevState.from) ||
      this.shouldUpdate(to, prevState.to)) {
      this.props.onChange({from, to, range});
    }
  }

  updateDates(range) {
    if (range !== DateRangeSelect.dateRanges.customRange) {
      const interval = rangeToInterval(range);
      this.setState({from: interval.from, to: interval.to});
    }
  }

  render() {
    const {spacing, meta, withTimeRange, maxRangeInDays} = this.props;
    const {from, range, to} = this.state;
    const {dateRanges} = DateRangeSelect;

    return (
      <Grid
        container
        spacing={spacing}
        sx={{alignItems: "flex-start"}}
      >
        {
          withTimeRange && (
            <Grid item xs={12} md={4}>
              <SelectField
                input={
                  {
                    value: range,
                    onChange: (event) => {
                      if (event.target.value !== dateRanges.customRange) {
                        this.updateDates(event.target.value);
                      }
                      this.setState({range: event.target.value});
                    }
                  }
                }
                label={
                  <FormattedMessage
                    id="shared.input.date.label"
                    defaultMessage="Date Range"
                  />
                }
                meta={{}}
              >
                <MenuItem value={dateRanges.customRange}>
                  <FormattedMessage
                    id="shared.input.date.customRange"
                    defaultMessage="Custom date range"
                  />
                </MenuItem>
                <MenuItem value={dateRanges.currentMonth}>
                  <FormattedMessage
                    id="shared.input.date.currentMonth"
                    defaultMessage="Current month"
                  />
                </MenuItem>
                <MenuItem value={dateRanges.lastMonth}>
                  <FormattedMessage
                    id="shared.input.date.lastMonth"
                    defaultMessage="Last month"
                  />
                </MenuItem>
                <MenuItem value={dateRanges.last30Days}>
                  <FormattedMessage
                    id="shared.input.date.last30days"
                    defaultMessage="Last 30 days"
                  />
                </MenuItem>
                <MenuItem value={dateRanges.last60Days}>
                  <FormattedMessage
                    id="shared.input.date.last60days"
                    defaultMessage="Last 60 days"
                  />
                </MenuItem>
                <MenuItem value={dateRanges.last90Days}>
                  <FormattedMessage
                    id="shared.input.date.last90days"
                    defaultMessage="Last 90 days"
                  />
                </MenuItem>
                <MenuItem value={dateRanges.last6Months}>
                  <FormattedMessage
                    id="shared.input.date.last6months"
                    defaultMessage="Last 6 months"
                  />
                </MenuItem>
                <MenuItem value={dateRanges.last12Months}>
                  <FormattedMessage
                    id="shared.input.date.last12months"
                    defaultMessage="Last 12 months"
                  />
                </MenuItem>
                <MenuItem value={dateRanges.currentYear}>
                  <FormattedMessage
                    id="shared.input.date.currentYear"
                    defaultMessage="Current year"
                  />
                </MenuItem>
                <MenuItem value={dateRanges.lastYear}>
                  <FormattedMessage
                    id="shared.input.date.lastYear"
                    defaultMessage="Last year"
                  />
                </MenuItem>
              </SelectField>
            </Grid>
          )
        }
        <Grid item xs={12} md={4}>
          <DateSelect
            id="date_range_from"
            label={
              <FormattedMessage
                id="shared.input.date.from"
                defaultMessage="From"
              />
            }
            value={from}
            minDate={maxRangeInDays > 0 && to ? subDays(to, maxRangeInDays) : undefined}
            maxDate={to}
            onChange={
              (date) => {
                this.setState({from: startOfDay(date), range: dateRanges.customRange});
              }
            }
            error={meta?.submitFailed && meta?.submitError?.from?.length > 0}
            helperText={meta?.submitError?.from?.join(', ')}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <DateSelect
            id="date_range_to"
            label={
              <FormattedMessage
                id="shared.input.date.to"
                defaultMessage="To"
              />
            }
            value={to}
            minDate={from}
            maxDate={maxRangeInDays > 0 && from ? addDays(from, maxRangeInDays) : undefined}
            onChange={
              (date) => {
                this.setState({to: endOfDay(date), range: dateRanges.customRange});
              }
            }
            error={meta?.submitFailed && meta?.submitError?.to?.length}
            helperText={meta?.submitError?.to?.join(', ')}
          />
        </Grid>
      </Grid>
    );
  }
}
